import {BasicDataTable, DataTableResponse} from "@/shared/ui/basicDataTable";
import {RequestParam} from "@/shared/api";
import {SelectableDataTable, SelectableDataTableConfig} from "@/shared/ui/selectableDataTable";
import {RecordedEntryDataTableBulkSelector} from "@/shared/ui/dataTableBulkSelector";
import {RecordingGpsPositionPopover} from "./recordingGpsPositionPopover";
import {htmlEscape} from "../utils/string";
import {formatCustomField, isWorkingTime, renderBreakTimePopover} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {
	formatDateTime,
	formatDuration,
	formatDurationDays,
	isSameDay,
	parseISOZonedDateTime,
	Temporal
} from "@/shared/utils/datefunctions";
import {DurationUnit, RecordedEntryStatus} from "@/shared/timrTypes";
import {shouldAddStatusNumber} from "@/shared/securityHelper";
import {getHighestSeverity, sortBySeverity} from "@/shared/validations/validationUtil";
import {CustomFieldDefinition, ReportRecord, ReportValidations, ValidationResult, WorkingTimeDateSpan} from "@/shared/timr";

export interface RecordingTableConfig extends SelectableDataTableConfig {
	dataUrl: string;
	allowQuickSwitch: boolean;
	allowResume: boolean;
	linkToReport?: string;
	customFieldDefinitions: CustomFieldDefinition[];
	statusNumbers: number[];
	allowedUserIds: number[];
	showUser: boolean;
	pageLength: number;
	paging: boolean;
	projectTimeAttachmentsEnabled?: boolean;
}

/**
 * Response payload of the `dataChanged` event for a `RecordingTable<T>`.
 *
 * `validations` is optional: working-/project-time endpoints send it only when saved
 * validations are enabled, and drive-log endpoints never send it.
 */
export interface RecordingDataTableResponse<T extends ReportRecord> extends DataTableResponse<T> {
	validations?: ReportValidations;
}

export abstract class RecordingTable<
	T extends ReportRecord,
	B extends RecordedEntryDataTableBulkSelector<T> = RecordedEntryDataTableBulkSelector<T>
> extends SelectableDataTable<T, number, B> {

	public static EVENT_RESUME_RECORDING = "resumeRecording";
	public static EVENT_QUICK_SWITCH_RECORDING = "quickSwitchRecording";
	public static EVENT_SHOW_RECORDING = "showRecording";
	public static EVENT_EDIT_RECORDING = "editRecording";
	public static EVENT_DELETE_RECORDING = "deleteRecording";
	public static EVENT_DUPLICATE_RECORDING = "duplicateRecording";
	public static EVENT_SHOW_REVISIONS = "showRevisions";
	public static EVENT_SHOW_CONFLICTS = "showConflicts";
	public static EVENT_MARK_CONFLICT_AS_RESOLVED = "markConflictAsResolved";
	public static EVENT_SET_STATUS = "setStatus";

	protected static ROW_CLASS_REVISION = "revision";
	protected static ROW_CLASS_CONFLICT = "conflict";

	private static ELEMENT_NAME_ROUNDING_MODE = "roundingMode";
	private static ELEMENT_NAME_ROUND_TO_MINUTES = "roundToMinutes";

	protected lastRecordingId: number;
	protected currentRunningRecording: boolean = false;
	protected isTaskLeader: boolean = false;

	constructor(public config: RecordingTableConfig) {
		super(config.dataUrl, config);
		this.init();
	}

	public setLastRecordingId(lastRecordingId: number) {
		this.lastRecordingId = lastRecordingId;
	}

	public setCurrentRunningRecording(runningRecording: boolean) {
		this.currentRunningRecording = runningRecording;
	}

	public setTaskLeader(isTaskLeader: boolean) {
		this.isTaskLeader = isTaskLeader;
	}

	public updateQuickSwitch() {
		if (this.currentRunningRecording) {
			$(".quickSwitchButton").show();
			$(".quickStartButton").hide();
		}
	}

	public updateSeverityHeaderIcon(validations: ValidationResult[]) {
		const columnHeader = this.config.element.find(".severityColumnHeader").empty();
		if (validations.length > 0) {
			columnHeader.append(
				$("<i>")
					.addClass(this.getSeverityIcon(validations))
					.attr("title", messages["validationResult.columnHeader.infoTooltip"]));
		}
	}

	protected getRoundingFilterParams(): RequestParam[] {
		return $.grep(this.filterData, (element, index) => {
			return element.name === RecordingTable.ELEMENT_NAME_ROUNDING_MODE ||
				element.name === RecordingTable.ELEMENT_NAME_ROUND_TO_MINUTES;
		});
	}

	protected isStatusNumberEnabled(statusNumber: RecordedEntryStatus, recording: ReportRecord): boolean {
		return statusNumber !== this.getRecordingStatus(recording);
	}

	protected quickSwitchRecording(recordingId: number) {
		$(this).triggerHandler(RecordingTable.EVENT_QUICK_SWITCH_RECORDING, [recordingId]);
	}

	protected resume(recording: ReportRecord) {
		$(this).triggerHandler(RecordingTable.EVENT_RESUME_RECORDING, [recording]);
	}

	protected show(recording: ReportRecord) {
		$(this).triggerHandler(RecordingTable.EVENT_SHOW_RECORDING, [recording]);
	}

	protected edit(recording: ReportRecord) {
		$(this).triggerHandler(RecordingTable.EVENT_EDIT_RECORDING, [recording]);
	}

	protected duplicate(recording: ReportRecord) {
		$(this).triggerHandler(RecordingTable.EVENT_DUPLICATE_RECORDING, [recording]);
	}

	protected delete(recording: ReportRecord) {
		$(this).triggerHandler(RecordingTable.EVENT_DELETE_RECORDING, [recording]);
	}

	protected showRevisions(recording: ReportRecord, tr: JQuery) {
		$(this).triggerHandler(RecordingTable.EVENT_SHOW_REVISIONS, [recording]);
	}

	protected showConflicts(recording: ReportRecord, tr: JQuery) {
		$(this).triggerHandler(RecordingTable.EVENT_SHOW_CONFLICTS, [recording]);
	}

	protected markConflictAsResolved(recording: ReportRecord) {
		$(this).triggerHandler(RecordingTable.EVENT_MARK_CONFLICT_AS_RESOLVED, [recording]);
	}

	protected setStatus(recording: ReportRecord, statusNumber: number) {
		$(this).triggerHandler(RecordingTable.EVENT_SET_STATUS, [recording, statusNumber]);
	}

	public static renderDuration(this: void, data: number, type: string, recording: ReportRecord, meta: DataTables.CellMetaSettings, revisionRow: boolean = false): string {
		const durationSpan = $("<span>");
		if (!revisionRow) {
			durationSpan.addClass("text-size-large");
		}
		if (isWorkingTime(recording) && (recording.editUnit === DurationUnit.Days || recording.editUnit === DurationUnit.HalfDays)) {
			durationSpan.append(formatDurationDays(recording.durationDays) + " ");
			durationSpan.append(recording.durationDays === 1 || recording.durationDays === 0.5 ? messages["report.unit.day"] : messages["report.unit.days"]);
		} else {
			durationSpan.text(formatDuration(data));
		}
		return durationSpan.prop("outerHTML") as string;
	}

	public static renderBreakTime(this: void, data: number, type: string, recording: ReportRecord, meta: DataTables.CellMetaSettings): string {
		if (isWorkingTime(recording) && (recording.editUnit === DurationUnit.Days || recording.editUnit === DurationUnit.HalfDays)) {
			return "";
		} else {
			return String(data);
		}
	}

	public static renderStatus(this: void, data: number, type: string, recording: ReportRecord, meta: DataTables.CellMetaSettings): string {
		return data ? "<i class=\"fal fa-status-" + data + "\" title=\"" + messages["reports.filter.status." + data] + "\"></i>" : "";
	}

	protected renderSubTable(recordings: ReportRecord[], parentRow: JQuery, rowClass: string, rowBuilder: ((recording: ReportRecord) => JQuery)) {
		const row = this.dataTable.row(parentRow);
		if (recordings) {
			const childRows: JQuery[] = [];
			$.each(recordings, (index, recording) => {
				const childRow = rowBuilder(recording);
				childRow.data("recording", recording);
				childRows.push(childRow);
			});
			row.child(childRows).show();
			parentRow.addClass("shown-" + rowClass);
		} else {
			row.child.hide();
			parentRow.removeClass((index, className) => {
				return (className.match(/(^|\s)shown-\S+/g) || []).join(" ");
			});
			parentRow.removeClass(rowClass);
		}
	}

	protected renderDateSpanSubTable(workingTimeDateSpans: WorkingTimeDateSpan[], parentRow: JQuery, rowClass: string, rowBuilder: ((workingTimeDateSpan: WorkingTimeDateSpan) => JQuery)) {
		const row = this.dataTable.row(parentRow);
		if (workingTimeDateSpans) {
			const childRows: JQuery[] = [];
			$.each(workingTimeDateSpans, (index, workingTimeDateSpan) => {
				const childRow = rowBuilder(workingTimeDateSpan);
				childRow.data("recording", workingTimeDateSpan);
				childRows.push(childRow);
			});
			row.child(childRows).show();
			parentRow.addClass("shown-" + rowClass);
		} else {
			row.child.hide();
			parentRow.removeClass((index, className) => {
				return (className.match(/(^|\s)shown-\S+/g) || []).join(" ");
			});
			parentRow.removeClass(rowClass);
		}
	}

	protected renderPopoverContent(recording: ReportRecord, rowClass?: string): string {
		let headlinedContent = "";
		let content = "";

		for (const definition of this.config.customFieldDefinitions) {
			if (definition?.fieldName) {
				const value = formatCustomField(recording[`customField${definition.fieldNumber}`], definition.fieldType);
				content += this.renderPopoverContentData(definition.fieldName, value);
			}
		}
		if (content) {
			headlinedContent += `<div class="text-left text-uppercase text-size-small text-grey">${messages["record.customFields.message"]}</div>`;
			headlinedContent += content;
		}
		return headlinedContent;
	}

	protected renderPopoverContentData(this: void, name: string, value: string, escapeHtml: boolean = true): string {
		const returnedName = escapeHtml ? htmlEscape(name) : name;
		return value ? `<div class="row">
						<div class="col-xs-5 text-semibold">${returnedName}</div>
						<div class="col-xs-7">${htmlEscape(value)}</div>
					</div>` : "";
	}

	protected createDataTableConfig(): DataTables.Settings {
		const domSetting = `rt<"datatable-footer"ip>`;

		return {
			pageLength: this.config.pageLength,
			paging: this.config.paging,
			buttons: {
				buttons: [
					{
						extend: "selectAll",
						className: "buttons-select-all",
						text: messages.withArguments("bulkselection.selectall", 0)
					},
					{
						extend: "selectNone",
						className: "buttons-select-none",
						text: messages["bulkselection.clear"]
					}
				]
			},
			dom: domSetting,
		};
	}

	private init() {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const that = this;
		// Only one popover per element, so define it on the parent
		this.config.element.parent().popover({
			trigger: "hover",
			placement: "top",
			selector: `td:not(td.slim-icon-column, td.gps-position, td.break-time-popover-anchor, td.context-menu, td[class^="border-row-"])`,
			container: ".content-wrapper",
			delay: {show: 500, hide: 0},
			html: true,
			template: "<div class=\"popover recording-popover\" role=\"tooltip\"><div class=\"arrow\"></div><div class=\"popover-content\"></div></div>",
			content: function (this: HTMLElement) {
				const td = $(this).closest("td");
				const recording = that.getRowData(td);
				const rowClass = td.closest("tr").attr("class");
				return recording ? that.renderPopoverContent(recording, rowClass) : null;
			}
		});
		this.config.element.on("click", "td.gps-position > a", (e) => {
			const tr = $(e.target as HTMLElement).closest("tr");
			const recording = this.getRowData(tr);
			this.show(recording);
		}).on("click", ".quickStartButton, .quickSwitchButton", (e) => {
			const tr = $(e.target as HTMLElement).closest("tr");
			const recording = this.getRowData(tr);
			this.quickSwitchRecording(recording.id);
		});

		if (this.config.linkToReport) {
			const recordingType = this.config.linkToReport;
			const url = "/timr/reports/" + recordingType + ".html";
			this.dataTable.on("draw.dt", () => {
				$(".datatable-footer")
					.after(`<div class="panel-footer">
											<div class="col-12 text-right">
												<a href="${url}" class="btn-link btn-link-primary btn-xs">${messages["recording.link.report"]}</a>
											</div>
										</div>`).remove();
			});
		}

		if (this.config.allowResume) {
			this.dataTable.on("draw.dt", () => {
				this.config.element.find("td.context-menu .dropdown").on("show.bs.dropdown", (e) => {
					const resume = $(e.target).find(`[data-action="report.resume"]`);
					if (!resume.hasClass("disabled")) {
						const td = $(e.target).closest("td");
						const recording = that.getRowData(td);
						if (!this.recordingResumable(recording)) {
							resume.addClass("disabled");
						}
					}
				});
			});
		}

		new RecordingGpsPositionPopover({
			baseElement: this.config.element,
			selector: "td.gps-position a",
			dataTable: this.dataTable,
			isRunningRecording: false
		});

		$(this).on(BasicDataTable.EVENT_DBLCLICK, (e, recording: ReportRecord) => {
			if (this.recordingChangeable(recording)) {
				this.edit(recording);
			} else {
				this.show(recording);
			}
		});

		this.initBreakTimePopover();
	}

	private initBreakTimePopover() {
		this.getDataTable().on("processing.dt", (_e, _settings, processing: boolean) => {
			if (!processing) {
				this.setBreakTimePopover();
			}
		});
	}

	private setBreakTimePopover() {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const that = this;

		// Popover can't update dynamically, so destroy and recreate it
		this.config.element.find("td.break-time-popover-anchor").popover("destroy").popover({
			trigger: "hover",
			placement: "top",
			container: ".content-wrapper",
			delay: {show: 500, hide: 0},
			html: true,
			template: `<div class="popover" style="min-width: 330px" role="tooltip"><div class="arrow"></div><div class="popover-content p-5"></div></div>`,
			content: function (this: HTMLElement) {
				const hoveredElement = $(this);
				const record = that.getRowData(hoveredElement);
				return record.breakTime ? renderBreakTimePopover(record) : null;
			}
		});
	}

	protected rowCallback(row: Node, recording: T, index: number) {
		super.rowCallback(row, recording, index);
		if (recording.conflicted) {
			$(row).addClass("conflicted");
		}
	}

	protected updateSelectedRowsCount(additionalSum?: string) {
		const selectedRowsCount = this.bulkSelector.getSelectedEntriesCount();
		const rowCountElement = $(".selected-rows-count");
		const rowSumElement = $(".selected-rows-sum");
		const selectedRowsDurationSum = formatDuration(this.bulkSelector.getSums().durationNetSum);
		const additionalSumMessage = additionalSum ?? "";

		if (this.bulkSelector.isAllEntriesSelected() && !this.bulkSelector.isNoneSelected()) {
			rowCountElement.text(messages.withArguments("bulkselection.allselected", selectedRowsCount));
			rowSumElement.text(messages.withArguments("bulkselection.duration", selectedRowsDurationSum) + additionalSumMessage);
			this.bulkSelector.selectAllCheckbox();
		} else if (this.bulkSelector.isNoneSelected()) {
			rowCountElement.text("");
			rowSumElement.text("");
			this.bulkSelector.unSelectAllCheckbox();
		} else if (this.bulkSelector.getSelectedEntriesCount() === 1) {
			rowCountElement.text(messages["bulkselection.oneselected"]);
			rowSumElement.text(messages.withArguments("bulkselection.duration", selectedRowsDurationSum) + additionalSumMessage);
			this.bulkSelector.unSelectAllCheckbox();
		} else {
			rowCountElement.text(messages.withArguments("bulkselection.entriesselected", selectedRowsCount));
			rowSumElement.text(messages.withArguments("bulkselection.duration", selectedRowsDurationSum) + additionalSumMessage);
			this.bulkSelector.unSelectAllCheckbox();
		}
	}

	protected abstract isDateTimeChangeAllowed(anyUser: boolean, userId: number, allowedUserIds: number[]): boolean;

	protected buildRevisionRow(recording: ReportRecord): JQuery {
		const date = parseISOZonedDateTime(recording.lastModified);

		return $("<tr></tr>").attr("role", "row").addClass(BasicDataTable.EXCLUDE_ROW_CLASS_DBLCLICK)
			.append($("<td></td>").attr("colspan", 100).addClass("td-modification-info")
				.append($("<div></div>")
					.append($("<span></span>").text(messages["report.column.lastModified"] + ": ").addClass("text-bold"))
					.append($("<span></span>").text(formatDateTime(date) + ", "))
					.append($("<span></span>").text(messages["report.column.modifiedByUser"] + ": ").addClass("text-bold"))
					.append($("<span></span>").text(recording.modifiedByUser))
					.addClass("div-modification-info")))
			.add(
				$("<tr></tr>").attr("role", "row").addClass(RecordingTable.ROW_CLASS_REVISION).addClass(BasicDataTable.EXCLUDE_ROW_CLASS_DBLCLICK)
					.append($("<td></td>").attr("colspan", this.config.allowSelect ? 2 : 1))
					.append(this.buildSubTableColumns(recording)));
	}

	protected buildConflictsRow(recording: ReportRecord): JQuery {
		return $("<tr></tr>").attr("role", "row").addClass(RecordingTable.ROW_CLASS_CONFLICT).addClass(BasicDataTable.EXCLUDE_ROW_CLASS_DBLCLICK)
			.append($("<td></td>").attr("colspan", this.config.allowSelect ? 2 : 1))
			.append(this.buildSubTableColumns(recording));
	}

	protected abstract buildSubTableColumns(recording: ReportRecord): JQuery[];

	protected buildEmptyColumn(): JQuery {
		return $("<td></td>");
	}

	protected buildTextColumn(text: string): JQuery {
		return $("<td></td>").text(text);
	}

	protected buildHtmlColumn(html: string, className: string = null): JQuery {
		return $("<td></td>").html(html).addClass(className);
	}

	protected renderQuickSwitch(this: void, data: RecordedEntryStatus, type: string, recording: ReportRecord, meta: unknown, title: string = "") {
		return `<div class="quickSwitchButtonContainer">
					<button class="btn btn-default quickStartButton" title="${messages.withArguments("recording.quickStart", htmlEscape(title))}">
						<a><i class="far fa-play"></i></a>
					</button>
					<button style="display: none" class="btn btn-default quickSwitchButton" title="${messages.withArguments("recording.quickSwitch", htmlEscape(title))}">
						<a><i class="far fa-exchange"></i></a>
					</button>
				</div>`;
	}

	protected buildDateTimeColumn(recording: ReportRecord, dateTimeString: string): JQuery {
		return $("<td></td>").addClass("pl-10 pr-10")
			.html(BasicDataTable.renderDateTimeWithTimeZone(dateTimeString, null, recording, null));
	}

	protected buildPositionColumn(recording: ReportRecord): JQuery {
		return $("<td></td>").addClass("no-padding gps-position")
			.html(BasicDataTable.renderPositionIcon(null, null, recording, null));
	}

	protected buildEmptyPositionColumn(): JQuery {
		return $("<td></td>").addClass("no-padding gps-position").html("");
	}

	protected buildNumberColumn(value: number | null): JQuery {
		// jQuery `.text(null)` renders an empty cell — same as `.text("")`. Used for nullable
		// numeric columns (drive-log mileages, distance for running drive logs).
		return $("<td></td>").addClass("text-right").text(value);
	}

	protected buildDurationColumn(recording: ReportRecord, duration: number): JQuery {
		return $("<td></td>").addClass("text-right").html(RecordingTable.renderDuration(duration, null, recording, null, true));
	}

	protected buildBreakTimeColumn(recording: ReportRecord, breakTime: number, className: string = "text-right"): JQuery {
		return $("<td></td>").addClass(className).text(RecordingTable.renderBreakTime(breakTime, null, recording, null));
	}

	protected buildIconColumn(html: string): JQuery {
		return this.buildHtmlColumn(html, "slim-icon-column");
	}

	protected renderSeverityIcon(validationResults: ValidationResult[]) {
		if (validationResults.length === 0) {
			return "";
		}

		return `<i class="${this.getSeverityIcon(validationResults)} fa-fw" title="${this.buildSeverityIconTitle(validationResults)}"></i>`;
	}

	/**
	 * Generates tooltip title for severity icon e.g.
	 * 		2 Errors:
	 * 		· Working time lasting over 24 hours
	 * 		· It was punched in at 12:00. However, work did not start until 11:00
	 * @param validationResults
	 * @private
	 */
	private buildSeverityIconTitle(validationResults: ValidationResult[]) {
		const {length} = validationResults;

		// build headline considering singular and plural e.g. "2 Validations:" followed by a line break
		const title = `${length} ${messages["validationResult.infoTooltip." + (length === 1 ? "validation" : "validations")]}: \n`;

		// add message of the validation results in form of a list
		const severities = sortBySeverity(validationResults)
			.map(v => `&#183 ${htmlEscape(messages.withArguments(v.messageCode, ...v.messageArguments))}`); // add middle dot char + message for each item

		// concat title and list of validations (seperated by line breaks)
		return title.concat(severities.join("\n"));
	}

	/**
	 * Returns severity icon for the highest severity in list
	 * Therefore it iterates through all severities of validations and returns the icon with the highest one
	 * @param validationResults
	 * @private
	 */
	private getSeverityIcon(validationResults: ValidationResult[]) {
		return "fa fa-validation-severity-" + getHighestSeverity(validationResults);
	}

	protected createContextMenu() {
		const menu = super.createContextMenu()
			.addMenuEntry({
				label: "report.resume",
				action: (recording) => this.resume(recording),
				show: this.config.allowResume,
				active: (recording) => this.recordingResumable(recording)
			})
			.addDivider()
			.addMenuEntry({
				label: "report.show",
				action: (recording) => this.show(recording),
			})
			.addMenuEntry({
				label: "report.edit",
				action: (recording) => this.edit(recording),
				active: (recording) => this.recordingChangeable(recording)
			})
			.addMenuEntry({
				label: "report.duplicate",
				action: (recording) => this.duplicate(recording),
				active: (recording) => this.recordingDuplicatable(recording)
			})
			.addMenuEntry({
				label: "report.delete",
				action: (recording) => this.delete(recording),
				active: (recording) => this.recordingDeletable(recording)
			})
			.addDivider()
			.addMenuEntry({
				label: "report.showRevisions",
				action: (recording, tr) => this.showRevisions(recording, tr),
				show: SecurityStatus.isRevisionsAllowed()
			})
			.addMenuEntry({
				label: "report.showConflicts",
				action: (recording, tr) => this.showConflicts(recording, tr),
				show: (recording) => this.isConflicted(recording)
			})
			.addMenuEntry({
				label: "report.markConflictAsResolved",
				action: (recording) => this.markConflictAsResolved(recording),
				active: (recording) => this.showMarkConflictAsResolved(recording),
				show: (recording) => this.isConflicted(recording)
			});

		if (this.config.statusNumbers.length !== 0) {
			menu.addDivider();

			this.config.statusNumbers.forEach((statusNumber) => {
				const statusName = messages["reports.filter.status." + statusNumber];
				const title = messages.withArguments("report.setStatus", statusName);

				menu.addMenuEntry({
					label: title,
					action: (recording: ReportRecord) => this.setStatus(recording, statusNumber),
					// DEVNOTE: It is very likely that the use and the delivered value of the variable `isTaskLeader` is basically not implemented correctly and consistently; see documentation of method
					show: (recording: ReportRecord) => shouldAddStatusNumber(statusNumber, this.getRecordingStatus(recording), this.isTaskLeader),
					active: (recording: ReportRecord) => this.isStatusNumberEnabled(statusNumber, recording)
				});
			});
		}

		return menu;
	}

	protected recordingResumable(recording: ReportRecord): boolean {
		return this.config.allowResume
			&& !this.currentRunningRecording
			&& this.getRecordingStatus(recording) === RecordedEntryStatus.Changeable
			&& this.isDateTimeChangeAllowed(false, recording.userId, this.config.allowedUserIds)
			&& this.lastRecordingId === recording.id
			&& isSameDay(parseISOZonedDateTime(recording.start), Temporal.Now.plainDateISO());
	}

	protected recordingDuplicatable(recording: ReportRecord): boolean {
		return this.isDateTimeChangeAllowed(false, recording.userId, this.config.allowedUserIds);
	}

	public recordingChangeable(recording: ReportRecord): boolean {
		return this.getRecordingStatus(recording) === RecordedEntryStatus.Changeable;
	}

	protected recordingDeletable(recording: ReportRecord): boolean {
		return this.getRecordingStatus(recording) === RecordedEntryStatus.Changeable
			&& this.isDateTimeChangeAllowed(false, recording.userId, this.config.allowedUserIds);
	}

	protected showMarkConflictAsResolved(recording: ReportRecord): boolean {
		return this.isDateTimeChangeAllowed(false, recording.userId, this.config.allowedUserIds)
	}

	protected abstract getRecordingStatus(recording: ReportRecord): RecordedEntryStatus;

	protected abstract isConflicted(recording: ReportRecord): boolean;
}
